// @flow

import { rem } from 'polished';
import React, { type Node } from 'react';
import styled, { type StyledComponent } from 'styled-components';

import { fontFamily, colorPalette, sizes, media } from '../../../styles/GlobalStyle';

import { Icon } from './Icon';

type Amount = {
  amount: string,
  size?: 'normal' | 'big',
};

type ButtonProps = {
  className?: string,
  children: Node,
  type?: 'button' | 'submit' | 'reset',
  picto?: string,
  pictoLocation?: 'left' | 'right',
  amount?: Amount,
  isDisabled?: boolean,
  onClick?: () => void,
  id?: string,
  defaultProps?: { [prop: 'primary']: any },
};

type ButtonStyledProps = {
  theme?: 'primary' | 'secondary',
};

const pictoElement = (picto?: string) => {
  return picto
    ? (
      <span className={'pictoWrapper'}>
        <Icon name={picto} />
      </span>
      )
    : null;
};

const amountElement = (amount?: Amount) => {
  let element = null;

  if (amount) {
    const classes = `amountWrapper ${amount.size ? amount.size : ''}`;
    element = <span className={classes}>{amount.amount}</span>;
  }
  return element;
};

const RawButton = ({
  className,
  children,
  type = 'button',
  picto,
  pictoLocation = 'right',
  amount,
  isDisabled = false,
  onClick,
  id = '',
}: ButtonProps) => {
  const pictoElt = pictoElement(picto);
  const amountElt = amountElement(amount);

  return (
    <button
      id={id}
      onClick={onClick}
      className={(className || '') + (isDisabled ? ' disabled' : '')}
      type={type}
    >
      <span className={'inner'}>
        {pictoLocation === 'left' && pictoElt}
        {children}
        {pictoLocation === 'right' && pictoElt}
        {amountElt}
      </span>
    </button>
  );
};

const getTheme = (theme) => {
  return typeof theme === 'string' ? theme : 'primary';
};

export const Button: StyledComponent<
  ButtonProps & ButtonStyledProps,
  {},
  typeof RawButton
> = styled(RawButton)`
  display: inline-block;
  width: 100%;
  height: 60px;
  border: none;
  border-radius: ${sizes.borderRadius};
  padding: 0 20px;
  background: ${({ theme }) => colorPalette[getTheme(theme)].active};
  color: ${({ theme }) => colorPalette[getTheme(theme)].foreGround};
  text-align: left;
  text-transform: uppercase;
  font-weight: ${fontFamily.primary.black.fontWeight};
  cursor: pointer;
  outline: 0;
  transition: 300ms all ease-out;

  position: relative;

  .inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    user-select: none;
  }

  .amountWrapper.big {
    font-size: ${rem(28)};
  }

  &:focus {
    border: solid 1px ${colorPalette.primary.active};
    box-sizing: border-box;
  }

  ${media.greaterThan('desktop')`
    &:hover {
      background-color: ${({ theme }) => colorPalette[getTheme(theme)].hover};
      &:after {
        opacity: 1;
        transform: scale(1, 1);
      }
    }
  `}

  &.disabled {
    opacity: 0.5;
  }

  &:active {
    transform: scale(0.98);
  }
`;

// TOTHINK: There is no other possibilty to set default value to styled component
// Button.defaultProps = {
//   theme: 'primary'
// };
